import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConnectionPreview } from "../../../types";
import type {
  ConnectionPreviewViewModel,
  ConnectionPreviewViewModelProps,
} from "./ConnectionPreview.ViewModel.types";

/**
 * Long by design. Each run hits the external source's API, so the preview waits for the
 * user to actually stop changing settings before spending a request.
 */
export const DEFAULT_PREVIEW_DEBOUNCE_MS = 10000;

const serialiseConfiguration = (configuration: unknown): string => {
  try {
    return JSON.stringify(configuration ?? {});
  } catch {
    return "";
  }
};

/**
 * Dry-runs the connection's task fetch against the configuration in the editor.
 *
 * Auto-refresh is debounced and only fires when the serialised configuration actually
 * differs from the one the current result came from, so re-renders and edits to unrelated
 * fields (name, colour) never spend a request.
 */
export const useConnectionPreviewViewModel = ({
  boardId,
  connectionId,
  configuration,
  boardsApi,
  enabled,
  debounceMs = DEFAULT_PREVIEW_DEBOUNCE_MS,
  loadErrorMessage,
}: ConnectionPreviewViewModelProps): ConnectionPreviewViewModel => {
  const [preview, setPreview] = useState<ConnectionPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasResult, setHasResult] = useState(false);

  const configurationKey = useMemo(
    () => serialiseConfiguration(configuration),
    [configuration],
  );

  // Held in refs so the debounce effect can depend on the serialised key alone — depending on
  // the configuration object itself would restart the timer on every render and never fire.
  const configurationRef = useRef(configuration);
  const configurationKeyRef = useRef(configurationKey);

  useEffect(() => {
    configurationRef.current = configuration;
    configurationKeyRef.current = configurationKey;
  });

  /** Configuration the in-flight or displayed result belongs to. */
  const requestedKeyRef = useRef<string | null>(null);
  const hasRunRef = useRef(false);
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const runPreview = useCallback(
    async (key: string, configurationToPreview: unknown): Promise<void> => {
      if (!boardId || !connectionId) {
        return;
      }

      abortRef.current?.abort();
      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      abortRef.current = controller;

      // Guards every state write below: a superseded or aborted request must not overwrite
      // the result of the one that replaced it.
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      requestedKeyRef.current = key;
      hasRunRef.current = true;
      setIsPending(false);
      setIsLoading(true);

      try {
        const result = await boardsApi.previewConnection(
          boardId,
          connectionId,
          configurationToPreview,
          controller?.signal,
        );

        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        setPreview(result);
        setErrorMessage(
          result.hasError ? result.errorMessage || loadErrorMessage : "",
        );
        setHasResult(true);
      } catch {
        if (requestId !== requestIdRef.current || !isMountedRef.current) {
          return;
        }

        setPreview(null);
        setErrorMessage(loadErrorMessage);
        setHasResult(true);
      } finally {
        if (requestId === requestIdRef.current && isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [boardId, boardsApi, connectionId, loadErrorMessage],
  );

  // Switching connection invalidates everything the previous one produced.
  useEffect(() => {
    abortRef.current?.abort();
    requestIdRef.current += 1;
    requestedKeyRef.current = null;
    hasRunRef.current = false;
    setPreview(null);
    setErrorMessage("");
    setHasResult(false);
    setIsLoading(false);
    setIsPending(false);
  }, [boardId, connectionId]);

  useEffect(() => {
    if (!enabled || !boardId || !connectionId) {
      setIsPending(false);
      return;
    }

    if (configurationKey === requestedKeyRef.current) {
      setIsPending(false);
      return;
    }

    // The first run is immediate — the user has just opened the form and wants to see
    // what the saved configuration currently finds without waiting.
    const delay = hasRunRef.current ? debounceMs : 0;
    setIsPending(delay > 0);

    const timer = setTimeout(() => {
      void runPreview(configurationKeyRef.current, configurationRef.current);
    }, delay);

    return () => clearTimeout(timer);
  }, [boardId, configurationKey, connectionId, debounceMs, enabled, runPreview]);

  const refresh = useCallback((): void => {
    if (!enabled) {
      return;
    }

    void runPreview(configurationKeyRef.current, configurationRef.current);
  }, [enabled, runPreview]);

  return {
    isLoading,
    isPending,
    hasResult,
    hasError: !!errorMessage,
    errorMessage,
    isInactive: preview?.isInactive ?? false,
    totalTasks: preview?.totalTasks ?? 0,
    newTasks: preview?.newTasks ?? 0,
    existingTasks: preview?.existingTasks ?? 0,
    archivedTasks: preview?.archivedTasks ?? 0,
    removedTasks: preview?.removedTasks ?? 0,
    skippedByAgeLimitTasks: preview?.skippedByAgeLimitTasks ?? 0,
    isTruncated: preview?.truncated ?? false,
    tasks: preview?.tasks ?? [],
    refresh,
  };
};
